import React, {useState, useCallback, useMemo} from 'react';
import {inject, observer} from 'mobx-react';
import {isDesktop, isDesktopSmall, promotionArticles} from 'app/utils';
import {createBlockConfig} from 'app/components/pages/casino/marketing-blocks/blockFactories';
import CasinoRandomSlot from 'app/components/pages/casino/marketing-blocks/CasinoRandomSlot';
import CasinoRecordWin from 'app/components/pages/casino/marketing-blocks/CasinoRecordWin';
import {
    CONTENT_TYPE,
    hasPageElementOfType,
    insertBlockAtOneBasedPosition,
    isActivePageElement,
    LAYOUT,
    MARKETING_BLOCK_KEY_ATTR,
    NOT_FOUND_INDEX,
    PRIORITY_FIRST_TYPES,
    RANDOM_SLOT_POSITION,
    RECORD_WIN_POSITION,
    useCasinoMarketingEffects
} from 'app/components/pages/casino/marketing-blocks/CasinoMarketingUtils';
import type {
    BlockConfig,
    CasinoMarketingBlocksProps,
    IframeGridPlacement,
    IframeRowShiftParams,
    PageElementsBuildOptions,
    SyntheticMarketingBlocks
} from 'app/components/pages/casino/marketing-blocks/CasinoMarketingBlocksTypes';
import type ShopItemInterface from 'app/interfaces/shop/ShopItemInterface';
import type {
    BigWinInterface,
    PageElementInterface,
    PageElementsMap
} from 'app/interfaces/stores/CasinoGamesStoreInterfaces';
import {isCoolOffActiveFor} from 'app/utils/isCoolOffActiveFor';

const ensurePriorityTypeFirst = (
    entries: Array<[string, PageElementInterface]>
): Array<[string, PageElementInterface]> => {
    if (entries.length === 0 || PRIORITY_FIRST_TYPES.has(entries[0][1].content_type)) {
        return entries;
    }

    const nearestPriorityIdx = entries.findIndex(
        ([, el], idx) => idx > 0 && PRIORITY_FIRST_TYPES.has(el.content_type)
    );

    if (nearestPriorityIdx === NOT_FOUND_INDEX) {
        return entries;
    }

    return [
        entries[nearestPriorityIdx],
        ...entries.slice(0, nearestPriorityIdx),
        ...entries.slice(nearestPriorityIdx + 1)
    ];
};

const promotePromotionsAfterTournament = (blocks: BlockConfig[]): BlockConfig[] => {
    const tournamentIdx = blocks.findIndex(b => b.contentType === CONTENT_TYPE.tournament);

    if (tournamentIdx === NOT_FOUND_INDEX) {
        return blocks;
    }

    const promotionsIdx = blocks.findIndex(b => b.contentType === CONTENT_TYPE.promotions && b.visible);

    if (promotionsIdx === NOT_FOUND_INDEX) {
        return blocks;
    }

    const next = [...blocks];
    const [promoBlock] = next.splice(promotionsIdx, 1);
    const insertIdx = promotionsIdx < tournamentIdx ? tournamentIdx - 1 : tournamentIdx;

    next.splice(insertIdx, 0, promoBlock);

    return next;
};

const buildBlocksFromPageElements = (pageElements: PageElementsMap, opts: PageElementsBuildOptions): BlockConfig[] => {
    const sortedEntries = Object.entries(pageElements)
        .filter((entry): entry is [string, PageElementInterface] => isActivePageElement(entry[1]))
        .sort((a, b) => a[1].position - b[1].position);

    return ensurePriorityTypeFirst(sortedEntries)
        .map(([, element]) => createBlockConfig(element, opts))
        .filter((b): b is BlockConfig => b !== null);
};

const buildOrderedMarketingBlocks = (
    pageElements: PageElementsMap,
    opts: PageElementsBuildOptions,
    synthetic: SyntheticMarketingBlocks
): BlockConfig[] => {
    let pageBlocks = buildBlocksFromPageElements(pageElements, opts);

    if (opts.tournament.isTournamentExpired) {
        pageBlocks = promotePromotionsAfterTournament(pageBlocks);
    }

    const {randomSlot, recordWin} = synthetic;

    const blocksWithRandomSlot = insertBlockAtOneBasedPosition(
        pageBlocks,
        randomSlot,
        RANDOM_SLOT_POSITION
    );

    return insertBlockAtOneBasedPosition(blocksWithRandomSlot, recordWin, RECORD_WIN_POSITION);
};

const applyIframeRowShift = (params: IframeRowShiftParams): number => {
    const IFRAME_ROW_SHIFT = 1;
    const {blockKey, blockRows, iframeRowStart, rowStart, skipShiftBlockKeys} = params;

    if (iframeRowStart === null || skipShiftBlockKeys.includes(blockKey)) {
        return rowStart;
    }

    const blockEndRow = rowStart + blockRows;

    if (rowStart >= iframeRowStart || blockEndRow > iframeRowStart) {
        return rowStart + IFRAME_ROW_SHIFT;
    }

    return rowStart;
};

const getIframePlacementForMarketing = (isGameActive?: boolean, iframeGridPlacement?: IframeGridPlacement | null): IframeGridPlacement | null => {
    if (!isGameActive || !isDesktop()) {
        return null;
    }

    return iframeGridPlacement ?? null;
};

const mapVisibleBlocksToGrid = (
    blocks: BlockConfig[],
    rowsBetweenBlocks: number,
    iframePlacement: IframeGridPlacement | null = null
): React.ReactElement[] => {
    let rowStart = 1;
    const iframeRowStart = iframePlacement?.rowStart ?? null;
    const skipShiftBlockKeys = iframePlacement?.skipShiftBlockKeys ?? [];

    return blocks
        .filter(b => b.visible)
        .map((b, slotIndex) => {
            const blockKey = `marketing-block-${slotIndex}`;
            const adjustedRowStart = applyIframeRowShift({
                blockKey,
                blockRows: b.rows,
                iframeRowStart,
                rowStart,
                skipShiftBlockKeys
            });
            const node = b.element as React.ReactElement<{style?: React.CSSProperties}>;
            const el = React.cloneElement(node, {
                [MARKETING_BLOCK_KEY_ATTR]: blockKey,
                key: blockKey,
                style: {...node.props?.style, gridRowStart: adjustedRowStart}
            } as React.HTMLAttributes<HTMLElement>);

            rowStart = adjustedRowStart + b.rows + rowsBetweenBlocks;

            return el;
        });
};

const buildRandomSlotBlock = (): BlockConfig => ({
    blockSize: 'small',
    contentType: CONTENT_TYPE.randomSlot,
    element: <CasinoRandomSlot />,
    rows: LAYOUT.randomSlotRows,
    visible: true
});

const buildRecordWinBlock = (biggestWin?: BigWinInterface): BlockConfig => ({
    blockSize: 'small',
    contentType: CONTENT_TYPE.recordWin,
    element: <CasinoRecordWin biggestWin={biggestWin} />,
    rows: LAYOUT.recordWinRows,
    visible: Boolean(biggestWin)
});

const CasinoMarketingBlocksView = observer((props: CasinoMarketingBlocksProps): React.ReactElement => {
    const {activeClickGame, articlesStore, biggestWins, cool_off, deactivateGame, iframeGridPlacement,
        isGameActive, onGoToTournamentGames, pageElements, scrollToFilter, setActiveGame, shopStore} = props;
    const [isTournamentExpired, setIsTournamentExpired] = useState(false);
    const onTournamentExpire = useCallback(() => setIsTournamentExpired(true), []);
    const hasLootboxElement = hasPageElementOfType(pageElements, CONTENT_TYPE.lootboxes);
    const hasPromotionsElement = hasPageElementOfType(pageElements, CONTENT_TYPE.promotions);
    const promoArticles = useMemo(
        () => hasPromotionsElement
            ? promotionArticles(articlesStore?.casinoPromoArticles ?? [])
            : [],
        [hasPromotionsElement, articlesStore?.casinoPromoArticles]
    );
    const casinoLootboxes = useMemo(
        () => hasLootboxElement
            ? shopStore?.casinoLootboxes.filter((item: ShopItemInterface) => Number(item.cost_money) > 0) ?? []
            : [],
        [hasLootboxElement, shopStore?.casinoLootboxes]
    );
    const biggestWin = useMemo(() => {
        const sorted = [...biggestWins].sort((a, b) => Number(b.amount) - Number(a.amount));
        return sorted[0];
    }, [biggestWins]);
    const [blockRows, rowsBetweenBlocks] = isDesktopSmall()
        ? [LAYOUT.marketingBlockRows, LAYOUT.rowsBetweenBlocks]
        : [LAYOUT.mobileBlockRows, LAYOUT.mobileRowsBetweenBlocks];

    const loadLootboxes = useCallback(() => {
        shopStore?.setShopUrl('/shop/lootboxes');
        shopStore?.updateShop(true);
    }, [shopStore]);

    const loadPromotions = useCallback(() => {
        articlesStore?.initPromoNew();
    }, [articlesStore]);

    const onUpdateShop = useCallback(() => shopStore?.updateShop(false), [shopStore]);

    useCasinoMarketingEffects(
        hasLootboxElement && casinoLootboxes.length === 0 ? loadLootboxes : null,
        hasPromotionsElement && promoArticles.length === 0 ? loadPromotions : null
    );

    const blocks = buildOrderedMarketingBlocks(
        pageElements ?? {},
        {
            layout: {blockRows},
            lootbox: {items: casinoLootboxes, onUpdateShop, scrollToFilter},
            newRelease: {
                activeClickGame,
                cool_off_present: isCoolOffActiveFor(cool_off, ['casino']),
                deactivateGame,
                setActiveGame
            },
            promotions: promoArticles,
            tournament: {isTournamentExpired, onGoToTournamentGames, onTournamentExpire}
        },
        {randomSlot: buildRandomSlotBlock(), recordWin: buildRecordWinBlock(biggestWin)}
    );

    return (
        <>
            {mapVisibleBlocksToGrid(
                blocks,
                rowsBetweenBlocks,
                getIframePlacementForMarketing(isGameActive, iframeGridPlacement)
            )}
        </>
    );
});

const CasinoMarketingBlocks = inject('articlesStore', 'shopStore')(CasinoMarketingBlocksView);

export default CasinoMarketingBlocks;
